import {
  get,
  set,
  push,
  update,
  remove,
  serverTimestamp,
  productsRef,
  productRef,
  snapshotToArray,
  snapshotToItem,
} from '@/firebase/database';
import type { Product, ProductFormData } from '@/types';
import { createNotification } from './notificationService';

export const getProducts = async (companyId: string): Promise<Product[]> => {
  const snap = await get(productsRef(companyId));
  const list = snapshotToArray<Product>(snap);
  return list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
};

export const getActiveProducts = async (companyId: string): Promise<Product[]> => {
  const all = await getProducts(companyId);
  return all.filter(p => p.status === 'active');
};

export const getProductById = async (companyId: string, id: string): Promise<Product | null> => {
  const snap = await get(productRef(companyId, id));
  if (!snap.exists()) return null;
  return snapshotToItem<Product>(snap);
};

export const createProduct = async (companyId: string, data: ProductFormData): Promise<string> => {
  const newRef = push(productsRef(companyId));
  const id = newRef.key as string;
  await set(newRef, {
    ...data,
    billingType: data.billingType || 'retail',
    id,
    companyId,
    status: 'active',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  createNotification({
    companyId,
    category: 'inventory',
    type: 'product_created',
    title: 'Product Added',
    message: `${data.name} (₹${data.price || 0}) was added to the product catalog`,
    link: `/products`,
  }).catch(() => {});

  return id;
};

export const updateProduct = async (
  companyId: string,
  id: string,
  data: Partial<ProductFormData>
): Promise<void> => {
  await update(productRef(companyId, id), {
    ...data,
    updatedAt: serverTimestamp(),
  });

  createNotification({
    companyId,
    category: 'inventory',
    type: 'product_updated',
    title: 'Product Updated',
    message: `Details updated for ${data.name || 'Product'}`,
    link: `/products`,
  }).catch(() => {});
};

export const deactivateProduct = async (companyId: string, id: string): Promise<void> => {
  await update(productRef(companyId, id), {
    status: 'inactive',
    updatedAt: serverTimestamp(),
  });
};

export const activateProduct = async (companyId: string, id: string): Promise<void> => {
  await update(productRef(companyId, id), {
    status: 'active',
    updatedAt: serverTimestamp(),
  });
};

export const deleteProduct = async (companyId: string, id: string): Promise<void> => {
  await remove(productRef(companyId, id));
  createNotification({
    companyId,
    category: 'inventory',
    type: 'product_deleted',
    title: 'Product Removed',
    message: `A product item was deleted from the inventory catalog`,
    link: `/products`,
  }).catch(() => {});
};

export const generateProductCode = (count: number): string => {
  return `PROD${String(count + 1).padStart(4, '0')}`;
};
